import { useState } from 'react'
import type { components } from '../../../api/generated/schema'
import {
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  SectionLabel,
  Textarea,
  type CardVariant,
} from '../../../design/components'
import { filterAbilities, useAbilityReference } from '../../reference/queries'
import { formatRefresh } from '../types'
import { AbilityDetailsModal, type AbilityLike } from './AbilityDetails'
import type { CharacterEditing } from '../useCharacterEditing'
import { SHEET_SECTION_ID } from './sheetSections'
import styles from './sections.module.css'

type AbilitiesSection = components['schemas']['AbilitiesSectionDto']
type Ability = components['schemas']['AbilityDto']
type AbilityType = components['schemas']['AbilityType']
type SpellcastingType = components['schemas']['SpellcastingType']
type SpellAccessLevel = components['schemas']['SpellAccessLevel']
type Shape = components['schemas']['ShapeDto']

const SPELLCASTING_TYPES: SpellcastingType[] = ['None', 'Evocation', 'Thaumaturgy', 'Both']
const SPELL_ACCESS_LEVELS: SpellAccessLevel[] = ['Full', 'Limited', 'Focused', 'None']

/**
 * Powers and stunts, collapsed by default so the top of the sheet stays fast to
 * scan (spec §5.1). Split by `type`, which the server already tells us.
 *
 * Adding goes through the *custom* ability endpoint rather than
 * `from-template/{abilityId}`: the API seeds a master list of abilities but
 * exposes no endpoint that returns it, so a client has no way to learn a template
 * id. Noted in the plan as an API gap.
 */
export function AbilitiesCard({
  abilities,
  shapeId,
  shapes = [],
  hasSpellcasting = false,
  editing,
}: {
  abilities: AbilitiesSection | undefined
  shapeId: number | null
  /** Every shape the character has. Empty or single = not a shapeshifter. */
  shapes?: Shape[]
  /**
   * The server's verdict on whether this character casts at all — their class, or an ability
   * that grants a discipline. It gates the Refinement templates, which buy nothing anyone
   * else can spend.
   */
  hasSpellcasting?: boolean
  editing?: CharacterEditing | undefined
}) {
  if (abilities === undefined) return null

  // Abilities carry their shape assignments; an ability with no shapes applies to
  // every shape. Filtering here is scoping, not derivation.
  const all = (abilities.abilities ?? []).filter((ability) => {
    const shapes = ability.shapeIds ?? []
    return shapes.length === 0 || shapeId === null || shapes.includes(shapeId)
  })

  const powers = all.filter((ability) => ability.type === 'Power')
  const stunts = all.filter((ability) => ability.type === 'Stunt')
  const canEdit = editing?.canEdit === true

  // Only a character who feeds has anything to spend on Hunger, so the flag is hidden for
  // everyone else. Read off the abilities rather than the class: someone turned mid-campaign
  // gets the toggle as soon as Feeding Dependency lands on their sheet.
  const feeds = (abilities.abilities ?? []).some((ability) => ability.grantsHungerTrack === true)

  return (
    <>
      <AbilityGroup
        id={SHEET_SECTION_ID.powers}
        label="Powers"
        variant="powers"
        type="Power"
        abilities={powers}
        shapes={shapes}
        feeds={feeds}
        hasSpellcasting={hasSpellcasting}
        {...(canEdit && editing !== undefined ? { editing } : {})}
      />
      <AbilityGroup
        id={SHEET_SECTION_ID.stunts}
        label="Stunts"
        variant="stunts"
        type="Stunt"
        abilities={stunts}
        shapes={shapes}
        feeds={feeds}
        hasSpellcasting={hasSpellcasting}
        {...(canEdit && editing !== undefined ? { editing } : {})}
      />
    </>
  )
}

function AbilityGroup({
  id,
  label,
  variant,
  type,
  abilities,
  shapes,
  feeds,
  hasSpellcasting,
  editing,
}: {
  id: string
  label: string
  variant: CardVariant
  type: AbilityType
  abilities: Ability[]
  shapes: Shape[]
  /** Whether the character has Feeding Dependency, which is what makes Hunger meaningful. */
  feeds: boolean
  /** Whether the character casts at all — gates the Refinement templates in the picker. */
  hasSpellcasting: boolean
  editing?: CharacterEditing
}) {
  const [adding, setAdding] = useState(false)
  // Which ability's full record is open, if any. One at a time: it is a dialog.
  const [details, setDetails] = useState<Ability | null>(null)
  const single = label.toLowerCase().replace(/s$/, '')

  // Without editing there is nothing to show for an empty group.
  if (abilities.length === 0 && editing === undefined) return null

  return (
    <CollapsibleCard
      id={id}
      empty={abilities.length === 0}
      variant={variant}
      label={label}
      summary={String(abilities.length)}
      action={
        editing !== undefined && !adding ? (
          <IconButton label={`Add ${single}`} icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {abilities.length === 0 && <p className={styles.empty}>None yet.</p>}

      {abilities.map((ability) => (
        <div key={ability.id} className={styles.row}>
          <div className={styles.rowHead}>
            <span className={styles.rowName}>{ability.name}</span>
            <span className={styles.rowBadge}>{formatRefresh(ability.refreshCost ?? 0)}</span>
            {/* Not gated on `editing`: the record is worth reading whether or not you
                are the one who can change it. */}
            <IconButton
              label={`Details for ${ability.name ?? single}`}
              icon="info"
              className={styles.rowDetails}
              onClick={() => setDetails(ability)}
            />
            {/* Beside Details rather than under the row. A labelled Remove button gave every
                ability a line of its own, and a card of nine powers paid for nine of them. */}
            {editing !== undefined && (
              <IconButton
                label={`Remove ${ability.name ?? single}`}
                icon="delete"
                className={styles.rowDelete}
                disabled={editing.removeAbility.isPending}
                onClick={() => editing.removeAbility.mutate({ abilityId: ability.id ?? 0 })}
              />
            )}
          </div>
          {ability.description !== undefined && ability.description !== '' && (
            /* Clamped to two lines: these read as a list, and a power with a paragraph behind
               it used to push the next one off the screen. Details has the whole text. */
            <p className={styles.abilityText}>{ability.description}</p>
          )}
          <AbilityTags ability={ability} />

          {editing !== undefined && shapes.length > 1 && (
            <ShapeAssignment ability={ability} shapes={shapes} editing={editing} />
          )}

          {editing !== undefined && feeds && (
            <label className={styles.rowToggle}>
              <input
                type="checkbox"
                checked={ability.isVampirismRelated === true}
                disabled={editing.setAbilityVampirismRelated.isPending}
                onChange={(e) =>
                  editing.setAbilityVampirismRelated.mutate({
                    abilityId: ability.id ?? 0,
                    isVampirismRelated: e.target.checked,
                  })
                }
              />
              <span>Vampirism — its refresh cost feeds Hunger</span>
            </label>
          )}
        </div>
      ))}

      {editing !== undefined && adding && (
        <AbilityForm
          type={type}
          hasSpellcasting={hasSpellcasting}
          editing={editing}
          onClose={() => setAdding(false)}
        />
      )}

      {details !== null && (
        <AbilityDetailsModal ability={details} shapes={shapes} onClose={() => setDetails(null)} />
      )}
    </CollapsibleCard>
  )
}

/**
 * Two ways to add: pick one of the seeded templates, or write a custom one.
 *
 * The template half is what `GET /reference/abilities` made possible — the ids
 * used to exist only in the database, so `from-template/{abilityId}` had nothing
 * a client could call it with.
 */
function AbilityForm({
  type,
  hasSpellcasting,
  editing,
  onClose,
}: {
  type: AbilityType
  /** Whether the character casts at all. Refinements are hidden from everyone else. */
  hasSpellcasting: boolean
  editing: CharacterEditing
  onClose: () => void
}) {
  const [custom, setCustom] = useState(false)
  const [search, setSearch] = useState('')
  // The template being read, which is not yet the template being taken.
  const [preview, setPreview] = useState<AbilityLike | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [refreshCost, setRefreshCost] = useState('1')
  const [spellcastingType, setSpellcastingType] = useState<SpellcastingType>('None')
  const [spellAccessLevel, setSpellAccessLevel] = useState<SpellAccessLevel>('Full')
  const [specializationScore, setSpecializationScore] = useState('1')
  const [magicItemScore, setMagicItemScore] = useState('2')

  const templates = useAbilityReference()
  // Refinement buys nothing but a bigger spellcasting budget, so for a character with no
  // discipline it is a refresh spent on an empty pool. Hidden rather than disabled: the
  // catalog is a list to scan, and a row that can never be taken is one more thing to read
  // past. The filter is here rather than in `filterAbilities` because it depends on the
  // character, not on what the endpoint would have returned.
  const matches = filterAbilities(templates.data ?? [], search, type).filter(
    (template) => hasSpellcasting || template.isRefinement !== true,
  )
  const pending = editing.addAbility.isPending || editing.addAbilityCustom.isPending

  if (!custom) {
    return (
      <div className={styles.inlineForm} style={{ marginTop: 'var(--sp-4)' }}>
        <SectionLabel tone="ember" micro>
          Add a {type.toLowerCase()}
        </SectionLabel>

        {/* The custom route sits at the top: it is a first-class choice, not a
            fallback you reach by scrolling past the whole catalog. */}
        <div className={styles.actions} style={{ marginTop: 0 }}>
          <Button variant="outline" onClick={() => setCustom(true)} disabled={pending}>
            Write a custom one
          </Button>
        </div>

        <Input
          label="Search the catalog"
          type="search"
          placeholder="Name or description…"
          value={search}
          autoFocus
          onChange={(event) => setSearch(event.target.value)}
        />

        {templates.isPending && <p className={styles.empty}>Loading the catalog…</p>}
        {templates.isError && <p className={styles.empty}>Could not load the ability catalog.</p>}

        {templates.isSuccess && matches.length === 0 && (
          <p className={styles.empty}>
            {search.trim() === ''
              ? `No ${type.toLowerCase()} templates are seeded.`
              : `Nothing matches “${search.trim()}”.`}
          </p>
        )}

        {matches.slice(0, 12).map((template) => (
          <div key={template.id} className={styles.templateRowGroup}>
            <button
              type="button"
              className={styles.templateRow}
              disabled={pending}
              onClick={() =>
                editing.addAbility.mutate({ abilityId: template.id ?? 0 }, { onSuccess: onClose })
              }
            >
              <span className={styles.rowHead}>
                <span className={styles.rowName}>{template.name}</span>
                <span className={styles.rowBadge}>{formatRefresh(template.refreshCost ?? 0)}</span>
              </span>
              {template.description !== undefined && template.description !== '' && (
                // Clamped: the catalog is a list to scan, and the whole text is a
                // tap away rather than three screens of scrolling.
                <span className={styles.templateText}>{template.description}</span>
              )}
            </button>
            {/* A sibling, not a child — a control inside the row's button would be a
                button inside a button, which is neither valid nor operable. */}
            <IconButton
              label={`Details for ${template.name ?? 'this template'}`}
              icon="info"
              onClick={() => setPreview(template)}
            />
          </div>
        ))}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
        </div>

        {preview !== null && (
          <AbilityDetailsModal
            ability={preview}
            onClose={() => setPreview(null)}
            action={
              <Button
                disabled={pending}
                onClick={() =>
                  editing.addAbility.mutate(
                    { abilityId: preview.id ?? 0 },
                    {
                      onSuccess: () => {
                        setPreview(null)
                        onClose()
                      },
                    },
                  )
                }
              >
                Add {preview.name}
              </Button>
            }
          />
        )}
      </div>
    )
  }

  const grantsSpellcasting = spellcastingType !== 'None'

  return (
    <form
      className={styles.inlineForm}
      style={{ marginTop: 'var(--sp-4)' }}
      onSubmit={(event) => {
        event.preventDefault()
        editing.addAbilityCustom.mutate(
          {
            name,
            description: description === '' ? null : description,
            type,
            refreshCost: Number(refreshCost) || 0,
            spellcastingType,
            // Access level only means anything alongside a discipline, and the
            // scores only exist to be spent by specializations and magic items.
            spellAccessLevel: grantsSpellcasting ? spellAccessLevel : 'None',
            specializationScore: grantsSpellcasting ? Number(specializationScore) || 0 : 0,
            magicItemScore: grantsSpellcasting ? Number(magicItemScore) || 0 : 0,
          },
          { onSuccess: onClose },
        )
      }}
    >
      <SectionLabel tone="ember" micro>
        New custom {type.toLowerCase()}
      </SectionLabel>

      <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Input
        label="Refresh cost"
        type="number"
        inputMode="numeric"
        value={refreshCost}
        onChange={(e) => setRefreshCost(e.target.value)}
        hint="What it spends from the refresh budget."
      />

      <Select
        label="Grants spellcasting"
        value={spellcastingType}
        options={SPELLCASTING_TYPES}
        onChange={setSpellcastingType}
      />

      {grantsSpellcasting && (
        <>
          <Select
            label="Access level"
            value={spellAccessLevel}
            options={SPELL_ACCESS_LEVELS}
            onChange={setSpellAccessLevel}
          />
          <Input
            label="Specialization points"
            type="number"
            inputMode="numeric"
            value={specializationScore}
            onChange={(e) => setSpecializationScore(e.target.value)}
            hint="Points this grants for spellcasting specializations."
          />
          <Input
            label="Magic-item points"
            type="number"
            inputMode="numeric"
            value={magicItemScore}
            onChange={(e) => setMagicItemScore(e.target.value)}
            hint="Points for focus and enchanted items, which share one pool."
          />
        </>
      )}

      <div className={styles.actions}>
        <Button variant="secondary" onClick={() => setCustom(false)} disabled={pending}>
          Back to catalog
        </Button>
        <Button type="submit" disabled={pending || name.trim() === ''}>
          {editing.addAbilityCustom.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </form>
  )
}

/** A labelled select over a fixed set of enum members. */
function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T
  options: readonly T[]
  onChange: (next: T) => void
}) {
  return (
    <label>
      <SectionLabel tone="muted" micro>
        {label}
      </SectionLabel>
      <select
        className={styles.levelSelect}
        value={value}
        onChange={(event) => onChange(event.target.value as T)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  )
}

/**
 * Which shapes an ability applies in — the shapeshifter-specific half.
 *
 * An empty assignment means *every* shape, which is why "All shapes" is a real
 * option rather than the absence of one: `Claws` belongs to the wolf, but
 * `Beast Change` belongs to the character. Only rendered when there is more than
 * one shape to choose between.
 */
function ShapeAssignment({
  ability,
  shapes,
  editing,
}: {
  ability: Ability
  shapes: Shape[]
  editing: CharacterEditing
}) {
  const assigned = ability.shapeIds ?? []
  const all = assigned.length === 0

  function toggle(shapeId: number) {
    const next = assigned.includes(shapeId)
      ? assigned.filter((id) => id !== shapeId)
      : [...assigned, shapeId]

    editing.setAbilityShapes.mutate({ abilityId: ability.id ?? 0, shapeIds: next })
  }

  return (
    <div className={styles.shapeAssign}>
      <SectionLabel tone="muted" micro>
        Applies in
      </SectionLabel>

      <div className={styles.shapeChecks}>
        <label className={styles.shapeCheck}>
          <input
            type="checkbox"
            checked={all}
            disabled={editing.setAbilityShapes.isPending}
            onChange={() =>
              editing.setAbilityShapes.mutate({ abilityId: ability.id ?? 0, shapeIds: [] })
            }
          />
          <span>All shapes</span>
        </label>

        {shapes.map((shape) => (
          <label key={shape.id} className={styles.shapeCheck}>
            <input
              type="checkbox"
              checked={!all && assigned.includes(shape.id ?? 0)}
              disabled={editing.setAbilityShapes.isPending}
              onChange={() => toggle(shape.id ?? 0)}
            />
            <span>{shape.name}</span>
          </label>
        ))}
      </div>
    </div>
  )
}

function AbilityTags({ ability }: { ability: Ability }) {
  const tags: string[] = []

  if (ability.spellcastingType !== undefined && ability.spellcastingType !== 'None') {
    tags.push(String(ability.spellcastingType))
  }
  if (ability.isVampirismRelated === true) {
    tags.push('Vampirism')
  }
  for (const extra of ability.extraStress ?? []) {
    tags.push(`+${extra.value ?? 0} ${String(extra.type ?? '')} stress`)
  }
  for (const extra of ability.extraConsequences ?? []) {
    tags.push(`+${String(extra.level ?? '')} ${String(extra.type ?? '')}`)
  }

  if (tags.length === 0) return null

  return (
    <div className={styles.rowTags}>
      {tags.map((tag) => (
        <span key={tag} className={styles.tag}>
          {tag}
        </span>
      ))}
    </div>
  )
}
